import React, { useState, useEffect } from 'react';
import { workoutHistoryService, type WorkoutHistoryFilters } from '../services/workoutHistory';
import { CommentDisplay } from './CommentDisplay';
import type { CompletedWorkout } from '../types';

interface WorkoutHistoryProps {
  onWorkoutSelect?: (workoutId: string) => void;
  onBack?: () => void;
}

/**
 * WorkoutHistory - Component for displaying workout history with filtering
 * Shows chronological list of completed workouts with search and filters
 * Requirements: 7.1, 7.2, 7.5
 */
export const WorkoutHistory: React.FC<WorkoutHistoryProps> = ({
  onWorkoutSelect,
  onBack
}) => {
  const [workouts, setWorkouts] = useState<CompletedWorkout[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minDuration, setMinDuration] = useState('');
  const [maxDuration, setMaxDuration] = useState('');
  const [hasNotes, setHasNotes] = useState<boolean | undefined>(undefined);
  const [sortBy, setSortBy] = useState<'date' | 'duration' | 'name'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    loadWorkouts(true);
  }, [searchQuery, dateFrom, dateTo, minDuration, maxDuration, hasNotes, sortBy, sortOrder]);

  const loadWorkouts = async (reset: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const filters: WorkoutHistoryFilters = {
        workoutName: searchQuery || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        minDuration: minDuration ? parseInt(minDuration) : undefined,
        maxDuration: maxDuration ? parseInt(maxDuration) : undefined,
        hasNotes,
        sortBy,
        sortOrder,
        limit: pageSize,
        offset: reset ? 0 : currentPage * pageSize
      };

      const result = await workoutHistoryService.getWorkoutHistory(filters);
      
      if (reset) {
        setWorkouts(result.workouts);
        setCurrentPage(0);
      } else {
        setWorkouts(prev => [...prev, ...result.workouts]);
      }
      
      setHasMore(result.hasMore);
      setTotalCount(result.totalCount);

    } catch (err) {
      console.error('Failed to load workout history:', err);
      setError('Не удалось загрузить историю тренировок');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = () => {
    if (!isLoading && hasMore) {
      setCurrentPage(prev => prev + 1);
      loadWorkouts(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setMinDuration('');
    setMaxDuration('');
    setHasNotes(undefined);
    setSortBy('date');
    setSortOrder('desc');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Сегодня';
    } else if (diffDays === 1) {
      return 'Вчера';
    } else if (diffDays < 7) {
      return `${diffDays} дн назад`;
    } else {
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `${hours}ч ${remainingMinutes}м`;
    }
    return `${minutes}м`;
  };

  if (error) {
    return (
      <div className="workout-history p-4 max-w-4xl mx-auto">
        <div className="text-center py-8">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => loadWorkouts(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="workout-history p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">История тренировок</h1>
          {totalCount > 0 && (
            <p className="text-sm text-gray-600">
              Найдено {totalCount} тренировок
            </p>
          )}
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-gray-600 p-2"
            title="Назад"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по названию тренировки..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Filter Toggle */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
          </button>

          {/* Sort */}
          <div className="flex items-center space-x-2">
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [newSortBy, newSortOrder] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
              }}
              className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="date-desc">Сначала новые</option>
              <option value="date-asc">Сначала старые</option>
              <option value="duration-desc">Длинные тренировки</option>
              <option value="duration-asc">Короткие тренировки</option>
              <option value="name-asc">По названию А-Я</option>
              <option value="name-desc">По названию Я-А</option>
            </select>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дата от
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дата до
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Duration Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Мин. длительность (мин)
                </label>
                <input
                  type="number"
                  value={minDuration}
                  onChange={(e) => setMinDuration(e.target.value)}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Макс. длительность (мин)
                </label>
                <input
                  type="number"
                  value={maxDuration}
                  onChange={(e) => setMaxDuration(e.target.value)}
                  placeholder="180"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Комментарии
                </label>
                <select
                  value={hasNotes === undefined ? '' : hasNotes.toString()}
                  onChange={(e) => {
                    const value = e.target.value;
                    setHasNotes(value === '' ? undefined : value === 'true');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Все</option>
                  <option value="true">С комментариями</option>
                  <option value="false">Без комментариев</option>
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Очистить фильтры
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Workout List */}
      {isLoading && workouts.length === 0 ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка истории...</p>
        </div>
      ) : workouts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-4">📋</div>
          <h2 className="text-xl font-semibold mb-2">История пуста</h2>
          <p className="text-gray-600">
            {searchQuery || dateFrom || dateTo || minDuration || maxDuration || hasNotes !== undefined
              ? 'Тренировки не найдены по заданным фильтрам'
              : 'Завершите первую тренировку, чтобы увидеть её здесь'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {workouts.map((workout) => (
            <div
              key={workout.id}
              className={`bg-white border border-gray-200 rounded-lg p-4 transition-colors ${
                onWorkoutSelect ? 'hover:border-blue-300 hover:bg-blue-50 cursor-pointer' : ''
              }`}
              onClick={() => onWorkoutSelect?.(workout.id)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{workout.workout_name}</h3>
                  <div className="flex items-center text-sm text-gray-600 mt-1">
                    <span>{formatDate(workout.completed_at)}</span>
                    <span className="mx-2">•</span>
                    <span>{formatDuration(workout.duration_minutes)}</span>
                  </div>
                </div>
                
                {onWorkoutSelect && (
                  <div className="text-blue-600 font-medium text-sm">
                    Подробнее →
                  </div>
                )}
              </div>

              {/* Workout Notes */}
              {workout.notes && (
                <div className="mb-3">
                  <CommentDisplay
                    comment={workout.notes}
                    type="workout"
                    compact={true}
                  />
                </div>
              )}

              {/* Workout Stats Preview */}
              <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t border-gray-100">
                <div className="flex items-center space-x-4">
                  <span>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {new Date(workout.completed_at).toLocaleTimeString('ru-RU', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
                
                <div className="text-xs text-gray-400">
                  ID: {workout.id.slice(-8)}
                </div>
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center py-4">
              <button
                onClick={loadMore}
                disabled={isLoading}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Загрузка...' : 'Загрузить ещё'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkoutHistory;